/**
 * Modelo del entorno en el que corre la web: qué entornos hay, con qué gravedad se tratan y qué
 * necesita la interfaz para señalizarlos.
 *
 * <h3>Por qué existe un «no identificado»</h3>
 * La app de escritorio deduce su entorno de la URL y da por **desarrollo** todo lo que no sea
 * producción (`electron/main.js`), porque allí el riesgo es el contrario: pintar el marco de dev
 * sobre datos reales. En la web ese default no vale. Los slots de preview se publican en los puertos
 * 9001-9099 del mismo servidor y uno de ellos —`preprod`— habla con la **base de datos de
 * producción**: un origen desconocido es, con más probabilidad, un slot que un entorno de juguete.
 * Por eso lo desconocido tiene estado propio y se trata con la gravedad de los datos reales.
 */

/** Los entornos que la web sabe distinguir. */
export type Entorno =
  | 'produccion'
  | 'desarrollo'
  | 'local'
  | 'slot'
  | 'preprod'
  | 'no-identificado';

/**
 * Cuánto cuidado hay que tener con lo que se toca. Es lo que decide el color, y por eso son tres y
 * no seis: al usuario le importa si puede trastear, no cómo se llama el servidor.
 */
export type GravedadEntorno =
  /** Producción. No se señaliza: la ausencia de marca ES la señal. */
  | 'ninguna'
  /** Desarrollo, local y slots de preview. Violeta. */
  | 'trasteable'
  /** Preprod y lo no identificado. Rojo. */
  | 'datos-reales';

/** Todo lo que la interfaz necesita saber del entorno, ya resuelto. */
export interface DescriptorEntorno {
  readonly entorno: Entorno;
  readonly gravedad: GravedadEntorno;

  /** Nombre del slot cuando `entorno` es `'slot'`; `null` en todo lo demás. */
  readonly slot: string | null;

  /** Texto del chip. Cadena vacía en producción, donde no se pinta nada. */
  readonly etiqueta: string;

  /**
   * Prefijo del título del documento, con su espacio final ya puesto. Cadena vacía en producción.
   * Va en mayúsculas y corto a propósito: tiene que leerse con la pestaña estrechada, y así casa
   * con el «TCG Factory ERP DEV» que ya usa la app de escritorio.
   */
  readonly prefijoTitulo: string;

  /** Explicación larga, para el tooltip y el `aria-label` del chip. */
  readonly descripcion: string;
}

/** Producción: el único entorno que no se señaliza por ningún canal. */
export const ENTORNO_PRODUCCION: DescriptorEntorno = {
  entorno: 'produccion',
  gravedad: 'ninguna',
  slot: null,
  etiqueta: '',
  prefijoTitulo: '',
  descripcion: '',
};

export const ENTORNO_DESARROLLO: DescriptorEntorno = {
  entorno: 'desarrollo',
  gravedad: 'trasteable',
  slot: null,
  etiqueta: 'Desarrollo',
  prefijoTitulo: '[DEV] ',
  descripcion: 'Estás en el entorno de desarrollo. Los datos no son reales.',
};

export const ENTORNO_LOCAL: DescriptorEntorno = {
  entorno: 'local',
  gravedad: 'trasteable',
  slot: null,
  etiqueta: 'Local',
  prefijoTitulo: '[LOCAL] ',
  descripcion: 'Estás en tu entorno local. Los datos no son reales.',
};

export const ENTORNO_PREPROD: DescriptorEntorno = {
  entorno: 'preprod',
  gravedad: 'datos-reales',
  slot: null,
  etiqueta: 'Preprod',
  prefijoTitulo: '[PREPROD] ',
  descripcion:
    'Estás en preproducción, que trabaja con los datos reales de producción. Lo que cambies aquí ' +
    'cambia de verdad.',
};

/**
 * Lo desconocido. El texto dice lo que sabemos —nada— y da la única instrucción útil: comprobar
 * dónde estás antes de tocar.
 */
export const ENTORNO_NO_IDENTIFICADO: DescriptorEntorno = {
  entorno: 'no-identificado',
  gravedad: 'datos-reales',
  slot: null,
  etiqueta: 'Entorno sin identificar',
  prefijoTitulo: '[ENTORNO ?] ',
  descripcion:
    'No reconocemos esta dirección, así que damos por hecho que los datos son reales. Comprueba en ' +
    'qué entorno estás antes de cambiar nada.',
};

/** Un slot de preview. Lleva su nombre porque hay varios vivos a la vez y hay que distinguirlos. */
export function entornoSlot(nombre: string): DescriptorEntorno {
  return {
    entorno: 'slot',
    gravedad: 'trasteable',
    slot: nombre,
    etiqueta: `Slot ${nombre}`,
    prefijoTitulo: `[SLOT ${nombre}] `,
    descripcion: `Estás en el slot de pruebas «${nombre}». Los datos no son reales.`,
  };
}
